import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const getCartKey = useCallback((id, size) => `${id}_${size || ''}`, []);

  const addToCart = useCallback((product) => {
    setCartItems(prev => {
      const key = getCartKey(product.id, product.selectedSize);
      const existing = prev.find(item => getCartKey(item.id, item.selectedSize) === key);
      if (existing) {
        return prev.map(item =>
          getCartKey(item.id, item.selectedSize) === key
            ? { ...item, quantity: (item.quantity || 1) + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  }, [getCartKey]);

  const removeFromCart = useCallback((productId, size) => {
    const key = getCartKey(productId, size);
    setCartItems(prev => prev.filter(item => getCartKey(item.id, item.selectedSize) !== key));
  }, [getCartKey]);

  const updateQuantity = useCallback((productId, size, quantity) => {
    if (quantity <= 0) { removeFromCart(productId, size); return; }
    const key = getCartKey(productId, size);
    setCartItems(prev => prev.map(item =>
      getCartKey(item.id, item.selectedSize) === key ? { ...item, quantity } : item
    ));
  }, [getCartKey, removeFromCart]);

  const getCartCount = useCallback(
    () => cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
    [cartItems]
  );

  const getCartTotal = useCallback(
    () => cartItems.reduce((sum, item) => sum + (item.precio || 0) * (item.quantity || 1), 0),
    [cartItems]
  );

  const clearCart = useCallback(() => setCartItems([]), []);

  const value = useMemo(() => ({
    cartItems, addToCart, removeFromCart, updateQuantity, getCartCount, getCartTotal, clearCart
  }), [cartItems, addToCart, removeFromCart, updateQuantity, getCartCount, getCartTotal, clearCart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
